import {
  useCallback,
  useRef,
  useState,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from "react";
import type { Auth } from "firebase/auth";
import type {
  DocumentData,
  Firestore,
  FirestoreError,
  QueryDocumentSnapshot,
} from "firebase/firestore";
import {
  collection,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  startAfter,
  where,
  Timestamp,
} from "firebase/firestore";
import type { PageKey } from "../lib/menu";

type Params = {
  auth: Auth;
  db: Firestore;
  pageSize: number;
  isCustomerPortal: boolean;
  customerKeys: string[];
  draftItemId: string | null;
  activePageRef: MutableRefObject<PageKey>;
  filters: Record<string, string>;
  createdAtRange?: { from: number | null; to: number | null };
  customerFilterOverride?: string[] | null;
};

type UseInventoryListResult<T extends { id: string }> = {
  items: T[];
  setItems: Dispatch<SetStateAction<T[]>>;
  pageHasNext: boolean;
  pageIndex: number;
  pageIsLoading: boolean;
  pageLastRefreshAt: number | null;
  hasNewTopItems: boolean;
  setHasNewTopItems: Dispatch<SetStateAction<boolean>>;
  fetchFirstPage: () => Promise<void>;
  fetchNextPage: () => Promise<void>;
  startHomeSentinel: () => void;
  stopHomeSentinel: () => void;
  lastError: string | null;
};

export function useInventoryList<T extends { id: string }>({
  auth,
  db,
  pageSize,
  isCustomerPortal,
  customerKeys,
  draftItemId,
  activePageRef,
  filters,
  createdAtRange,
  customerFilterOverride,
}: Params): UseInventoryListResult<T> {
  const [items, setItems] = useState<T[]>([]);
  const [pageLastDoc, setPageLastDoc] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [pageHasNext, setPageHasNext] = useState<boolean>(false);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageIsLoading, setPageIsLoading] = useState(false);
  const [hasNewTopItems, setHasNewTopItems] = useState(false);
  const [pageLastRefreshAt, setPageLastRefreshAt] = useState<number | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);

  const querySigRef = useRef<string>("");
  const homeSentinelUnsub = useRef<null | (() => void)>(null);
  const lastRefreshAtRef = useRef<number>(0);

  const stopHomeSentinel = useCallback(() => {
    if (homeSentinelUnsub.current) {
      homeSentinelUnsub.current();
      homeSentinelUnsub.current = null;
    }
  }, []);

  const getEqFilters = useCallback((): Array<[string, string | boolean]> => {
    const entries: Array<[string, string | boolean]> = [];
    Object.entries(filters || {}).forEach(([key, raw]) => {
      const v = (raw || "").toString().trim();
      if (!v) return;
      if (key === "createdAt") return; // håll datum-sökning klient-side tills vi har serverstöd

      if (key === "chargerIncluded") {
        const low = v.toLowerCase();
        const truthy = ["ja", "yes", "y", "true", "1"];
        const falsy = ["nej", "no", "n", "false", "0"];
        if (truthy.includes(low)) entries.push([key, true]);
        else if (falsy.includes(low)) entries.push([key, false]);
        return;
      }

      entries.push([key, v]);
    });
    entries.sort((a, b) => a[0].localeCompare(b[0]));
    return entries;
  }, [filters]);

  const computeQuerySignature = useCallback((): string => {
    const filterSig = getEqFilters().map(([k, v]) => `${k}=${String(v)}`).join("&");
    const createdSig = createdAtRange ? `${createdAtRange.from || ""}-${createdAtRange.to || ""}` : "";
    if (isCustomerPortal) {
      const keys = Array.isArray(customerKeys) ? [...customerKeys].filter(Boolean).sort() : [];
      return `cust:${keys.join("|")}|${filterSig}|${createdSig}`;
    }
    return `staff|${filterSig}|${createdSig}`;
  }, [isCustomerPortal, customerKeys, getEqFilters, createdAtRange]);

  const applyCustomerFilter = useCallback((base: any) => {
    const overrideIds = Array.isArray(customerFilterOverride) ? customerFilterOverride.filter(Boolean) : null;
    if (overrideIds && overrideIds.length > 0) {
      if (overrideIds.length === 1) {
        return query(base, where("customerId", "==", overrideIds[0]));
      }
      const top10 = overrideIds.slice(0, 10);
      if (overrideIds.length > 10) console.warn("customerFilterOverride > 10; använder de första 10.");
      return query(base, where("customerId", "in", top10));
    }

    if (!isCustomerPortal) return base;

    const keys = Array.isArray(customerKeys) ? customerKeys.filter(Boolean) : [];

    if (keys.length === 0) {
      return query(base, where("customerId", "==", "__no_customer__"));
    }

    if (keys.length === 1) {
      return query(base, where("customerId", "==", keys[0]));
    }

    const top10 = keys.slice(0, 10);
    if (keys.length > 10) {
      console.warn("customerKeys > 10; visar de första 10 tills vidare.");
    }
    return query(base, where("customerId", "in", top10));
  }, [isCustomerPortal, customerKeys]);

  const applyFilters = useCallback((base: any) => {
    let q = base;
    const eqFilters = getEqFilters();
    eqFilters.forEach(([key, val]) => {
      if (key === "chargerIncluded") {
        q = query(q, where(key, "==", val === true));
      } else {
        q = query(q, where(key, "==", val));
      }
    });

    if (createdAtRange?.from) {
      q = query(q, where("createdAt", ">=", Timestamp.fromMillis(createdAtRange.from)));
    }
    if (createdAtRange?.to) {
      // exclusive upper bound
      q = query(q, where("createdAt", "<", Timestamp.fromMillis(createdAtRange.to)));
    }
    return q;
  }, [getEqFilters, createdAtRange]);

  const fetchFirstPage = useCallback(async () => {
    if (!auth.currentUser) {
      stopHomeSentinel?.();
      setItems([]);
      setPageLastDoc(null);
      setPageHasNext(false);
      setPageIndex(1);
      setHasNewTopItems(false);
      setPageLastRefreshAt(Date.now());
      return;
    }

    setPageIsLoading(true);
    setLastError(null);

    const sig = computeQuerySignature();
    querySigRef.current = sig;

    try {
      let q = query(collection(db, "itInventory"));
      q = applyCustomerFilter(q);
      q = applyFilters(q);
      if (createdAtRange?.from || createdAtRange?.to) {
        q = query(q, orderBy("createdAt", "desc"), orderBy("stateSort", "asc"), orderBy("updatedAt", "desc"));
      } else {
        q = query(q, orderBy("stateSort", "asc"), orderBy("updatedAt", "desc"), orderBy("createdAt", "desc"));
      }
      q = query(q, limit(pageSize));

      const snap = await getDocs(q);
      if (querySigRef.current !== sig) return;

      const rows = snap.docs.map(d => ({ id: d.id, ...(d.data() as any) })) as T[];
      setItems(rows);
      setPageLastDoc(snap.docs.length ? snap.docs[snap.docs.length - 1] : null);
      setPageHasNext(snap.docs.length === pageSize);
      setPageIndex(1);
      setHasNewTopItems(false);
      setPageLastRefreshAt(Date.now());
    } catch (e) {
      console.error(e);
      const msg = (e as any)?.message || "Kunde inte ladda listan";
      setLastError(msg);
    } finally {
      setPageIsLoading(false);
    }
  }, [
    auth,
    applyCustomerFilter,
    applyFilters,
    computeQuerySignature,
    db,
    pageSize,
    stopHomeSentinel,
    createdAtRange,
  ]);

  const fetchNextPage = useCallback(async () => {
    if (!auth.currentUser) return;
    if (!pageLastDoc) return;

    setPageIsLoading(true);
    setLastError(null);
    const sig = computeQuerySignature();

    try {
      let q = query(collection(db, "itInventory"));
      q = applyCustomerFilter(q);
      q = applyFilters(q);
      if (createdAtRange?.from || createdAtRange?.to) {
        q = query(q, orderBy("createdAt", "desc"), orderBy("stateSort", "asc"), orderBy("updatedAt", "desc"));
      } else {
        q = query(q, orderBy("stateSort", "asc"), orderBy("updatedAt", "desc"), orderBy("createdAt", "desc"));
      }
      q = query(q, startAfter(pageLastDoc), limit(pageSize));

      const snap = await getDocs(q);
      if (querySigRef.current !== sig) return;

      const rows = snap.docs.map(d => ({ id: d.id, ...(d.data() as any) })) as T[];
      setItems(prev => [...prev, ...rows]);
      setPageLastDoc(snap.docs.length ? snap.docs[snap.docs.length - 1] : null);
      setPageHasNext(snap.docs.length === pageSize);
      setPageIndex(p => p + 1);
    } catch (e) {
      console.error(e);
      const msg = (e as any)?.message || "Kunde inte ladda fler rader";
      setLastError(msg);
    } finally {
      setPageIsLoading(false);
    }
  }, [
    auth,
    applyCustomerFilter,
    applyFilters,
    computeQuerySignature,
    pageLastDoc,
    pageSize,
    createdAtRange,
  ]);

  const startHomeSentinel = useCallback(() => {
    if (!auth.currentUser) {
      stopHomeSentinel();
      return;
    }

    stopHomeSentinel();

    const constraints: any[] = [orderBy("updatedAt", "desc"), limit(1)];

    if (isCustomerPortal) {
      const keys = Array.isArray(customerKeys) ? customerKeys.filter(Boolean) : [];
      if (keys.length === 0) {
        constraints.push(where("customerId", "==", "__no_customer__"));
      } else if (keys.length === 1) {
        constraints.push(where("customerId", "==", keys[0]));
      } else {
        const top10 = keys.slice(0, 10);
        if (keys.length > 10) console.warn("customerKeys > 10; lyssnar på första 10.");
        constraints.push(where("customerId", "in", top10));
      }
    }

    const q = query(collection(db, "itInventory"), ...constraints);

    let first = true;
    homeSentinelUnsub.current = onSnapshot(
      q,
      (snap) => {
        if (first) { first = false; return; }
        const topDoc = snap.docs[0];
        if (!topDoc) return;
        const d = topDoc.data() as any;
        if (d?.isDraft === true) return;
        if (draftItemId && topDoc.id === draftItemId) return;
        const now = Date.now();
        if (now - lastRefreshAtRef.current < 1500) return;
        if (activePageRef.current !== "home") return;
        if (pageIndex <= 1) {
          lastRefreshAtRef.current = now;
          fetchFirstPage();
        } else {
          setHasNewTopItems(true);
        }
      },
      (err: FirestoreError) => {
        if (err?.code === "permission-denied") {
          stopHomeSentinel();
          return;
        }
        console.warn("Home sentinel snapshot error:", err);
      }
    );
  }, [
    activePageRef,
    auth,
    customerKeys,
    draftItemId,
    fetchFirstPage,
    isCustomerPortal,
    pageIndex,
    stopHomeSentinel,
  ]);

  return {
    items,
    setItems,
    pageHasNext,
    pageIndex,
    pageIsLoading,
    pageLastRefreshAt,
    hasNewTopItems,
    setHasNewTopItems,
    fetchFirstPage,
    fetchNextPage,
    startHomeSentinel,
    stopHomeSentinel,
    lastError,
  };
}
